// @ts-strict-ignore
import React, { useEffect, useRef } from 'react'
import { UseFormRegister, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Combobox,
  ComboboxGroup,
  ComboboxGroupItem,
  ComboboxItem,
  Input,
} from '@node-space/ui-components'
import { scrollToTopError } from 'pages/EmbeddedCustomers/helpers/utils'
import { AddCustomerFormErrors, AddCustomerPayload } from 'types/customers'
import { CustomerAddress } from '../CustomerAddress'

interface BusinessCustomerFieldsProps {
  businessName?: string
  businessDescription?: string
  registrationNumber?: string
  businessTaxResidency?: string
  countries?: ComboboxItem[]
  isCountriesLoading?: boolean
  businessMonthlyVolume?: string
  monthlyVolumesOptions?: ComboboxItem[]
  isFetchingMonthlyVolumes?: boolean
  industryOptions?: ComboboxGroupItem[]
  isFetchingIndustries?: boolean
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
  countryCode?: string
  industry?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  nationality?: string
  errors?: AddCustomerFormErrors
  register?: UseFormRegister<AddCustomerPayload>
  setValue?: (name: string, value: string) => void
  states?: ComboboxItem[]
  isFetchingStates?: boolean
  stateCode?: string
  isAllowedCountriesLoading?: boolean
  allowedCountries?: ComboboxItem[]
}

export const BusinessCustomerFields = ({
  businessName,
  businessDescription,
  registrationNumber,
  businessTaxResidency,
  countries,
  isCountriesLoading,
  businessMonthlyVolume,
  monthlyVolumesOptions,
  isFetchingMonthlyVolumes,
  industryOptions,
  isFetchingIndustries,
  addressLine1,
  addressLine2,
  city,
  postalCode,
  countryCode,
  industry,
  firstName,
  lastName,
  dateOfBirth,
  nationality,
  errors,
  register,
  setValue,
  states,
  isFetchingStates,
  stateCode,
  isAllowedCountriesLoading,
  allowedCountries,
}: BusinessCustomerFieldsProps) => {
  const { t } = useTranslation()

  const handleCountryTextResidencySelect = (countryCode: string) => {
    const taxResidencyName = countries.find(country => country?.value === countryCode)
      ?.leftText as string
    setValue('businessTaxResidency', countryCode)
    setValue('businessTaxResidencyName', taxResidencyName)
  }
  const handleIndustryChange = value => {
    const industryName = industryOptions
      ?.flatMap(industry => industry?.items)
      .find(item => item?.value === value)?.label
    setValue('industry', value)
    setValue('industryName', industryName)
  }

  const handleNationalitySelect = (countryCode: string) => {
    const nationalityName = countries.find(country => country?.value === countryCode)
      ?.leftText as string
    setValue('nationality', countryCode)
    setValue('nationalityName', nationalityName)
  }

  const handleMonthlyEstimateVolumesSelect = (value: string) => {
    const businessMonthlyVolumeName = monthlyVolumesOptions.find(
      monthlyVolume => monthlyVolume?.value === value
    )?.leftText as string
    setValue('businessMonthlyVolume', value)
    setValue('businessMonthlyVolumeName', businessMonthlyVolumeName)
  }

  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const formLike = {
    formState: { errors: errors ?? {} },
  } as unknown as UseFormReturn<Record<string, unknown>>

  useEffect(() => {
    if (!errors) return
    scrollToTopError(formLike, fieldRefs)
  }, [errors])

  return (
    <Box flexCol gapY={16}>
      <Box
        ref={el => {
          fieldRefs.current.businessName = el
        }}
      >
        <Input
          {...register('businessName')}
          label={t('customers.businessName')}
          inputMode="text"
          name="businessName"
          testId="business-name"
          errorText={errors?.businessName?.message}
          error={!!errors?.businessName}
          value={businessName}
          onChange={e => setValue('businessName', e?.target?.value)}
        />
      </Box>
      <Box
        ref={el => {
          fieldRefs.current.industry = el
        }}
      >
        <ComboboxGroup
          id="industry"
          items={industryOptions}
          {...register('industry')}
          disabled={isFetchingIndustries}
          label={t('customers.industry')}
          selectPlaceholder={t('customers.industryPlaceholder')}
          onChange={handleIndustryChange}
          errorText={errors?.industry?.message}
          defaultValue={industry}
        />
      </Box>
      <Box
        ref={el => {
          fieldRefs.current.businessDescription = el
        }}
      >
        <Input
          {...register('businessDescription')}
          label={t('customers.businessDescription')}
          inputMode="text"
          name="businessDescription"
          testId="business-description"
          errorText={errors?.businessDescription?.message}
          error={!!errors?.businessDescription}
          value={businessDescription}
          onChange={e => setValue('businessDescription', e?.target?.value)}
        />
      </Box>
      <Box flex gapX={16}>
        <Box
          ref={el => {
            fieldRefs.current.registrationNumber = el
          }}
          flexSize="auto"
          width="full"
        >
          <Input
            {...register('registrationNumber')}
            label={t('customers.businessRegistrationNumber')}
            inputMode="text"
            name="registrationNumber"
            testId="business-registration-number"
            errorText={errors?.registrationNumber?.message}
            error={!!errors?.registrationNumber}
            value={registrationNumber}
            onChange={e => setValue('registrationNumber', e?.target?.value)}
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.businessTaxResidency = el
          }}
          flexSize="auto"
          width="full"
        >
          <Combobox
            {...register('businessTaxResidency')}
            items={allowedCountries}
            loading={isAllowedCountriesLoading}
            label={t('customers.businessTaxResidency')}
            onChange={handleCountryTextResidencySelect}
            id="businessTaxResidency"
            errorText={errors?.businessTaxResidency?.message}
            defaultValue={businessTaxResidency}
          />
        </Box>
      </Box>
      <Box
        ref={el => {
          fieldRefs.current.businessMonthlyVolume = el
        }}
      >
        <Combobox
          id="businessMonthlyVolume"
          items={monthlyVolumesOptions}
          {...register('businessMonthlyVolume')}
          label={t('customers.businessMonthlyVolume')}
          onChange={handleMonthlyEstimateVolumesSelect}
          errorText={errors?.businessMonthlyVolume?.message}
          defaultValue={businessMonthlyVolume}
          loading={isFetchingMonthlyVolumes}
        />
      </Box>

      <CustomerAddress
        title={t('customers.registeredAddress')}
        errors={errors}
        addressLine1={addressLine1}
        addressLine2={addressLine2}
        city={city}
        postalCode={postalCode}
        countryCode={countryCode}
        countries={allowedCountries}
        isCountriesLoading={isAllowedCountriesLoading}
        register={register}
        setValue={setValue}
        stateCode={stateCode}
        states={states}
        isStatesLoading={isFetchingStates}
      />

      <h2 className="text-h2 text-foreground-prominent">{t('customers.representative.title')}</h2>
      <Box flex gapX={16}>
        <Box
          ref={el => {
            fieldRefs.current.firstName = el
          }}
          flexSize="auto"
          width="full"
        >
          <Input
            {...register('firstName')}
            label={t('customers.representative.firstName')}
            inputMode="text"
            name="firstName"
            testId="customer-firstName"
            errorText={errors?.firstName?.message}
            error={!!errors?.firstName}
            value={firstName}
            onChange={e => setValue('firstName', e?.target?.value)}
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.lastName = el
          }}
          flexSize="auto"
          width="full"
        >
          <Input
            {...register('lastName')}
            label={t('customers.representative.lastName')}
            inputMode="text"
            name="lastName"
            testId="customer-lastName"
            errorText={errors?.lastName?.message}
            error={!!errors?.lastName}
            value={lastName}
            onChange={e => setValue('lastName', e?.target?.value)}
          />
        </Box>
      </Box>
      <Box flex gapX={16}>
        <Box
          ref={el => {
            fieldRefs.current.dateOfBirth = el
          }}
          flexSize="auto"
          width="full"
        >
          <Input
            {...register('dateOfBirth')}
            label={t('customers.representative.dateOfBirth')}
            inputMode="text"
            placeholder="YYYY-MM-DD"
            name="dateOfBirth"
            testId="customer-dateOfBirth"
            errorText={errors?.dateOfBirth?.message}
            error={!!errors?.dateOfBirth}
            value={dateOfBirth}
            onChange={e => setValue('dateOfBirth', e?.target?.value)}
          />
        </Box>
        <Box
          ref={el => {
            fieldRefs.current.nationality = el
          }}
          flexSize="auto"
          width="full"
        >
          <Combobox
            {...register('nationality')}
            items={countries}
            loading={isCountriesLoading}
            label={t('customers.representative.nationality')}
            onChange={handleNationalitySelect}
            id="nationality"
            optionalText={t('optional')}
            errorText={errors?.nationality?.message}
            defaultValue={nationality}
          />
        </Box>
      </Box>
    </Box>
  )
}
